import { useState } from 'react';
import { ChevronDown, ChevronUp, Minus, Plus, Search, X } from 'lucide-react';
import { canZoomIn, canZoomOut, zoomIn, zoomLabel, zoomOut } from './zoom';
import styles from './document-viewer.module.css';

// The page box shows where you are while you scroll, and takes a number to jump
// to. It only commits on Enter or leaving the box, so typing "12" doesn't jump
// to page 1 on the way.
const PageInput = ({ current, total, onGo }) => {
  const [draft, setDraft] = useState(null);

  const commit = () => {
    const page = Number.parseInt(draft, 10);
    if (Number.isFinite(page)) onGo(Math.min(Math.max(page, 1), total));
    setDraft(null);
  };

  return (
    <div className={styles.pageBox}>
      <input
        className={styles.pageInput}
        inputMode="numeric"
        aria-label={`Page, ${total} in total`}
        value={draft ?? String(current)}
        onFocus={(e) => {
          setDraft(String(current));
          e.target.select();
        }}
        onChange={(e) => setDraft(e.target.value.replace(/\D/g, ''))}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
        }}
        style={{ width: `${String(total).length + 1}ch` }}
      />
      <span className={styles.pageTotal}>/ {total}</span>
    </div>
  );
};

// The floating bar at the bottom of the frame. Each section is optional: a PDF
// gets search, pages and zoom; an image gets zoom only.
export const Toolbar = ({ page, zoom, search }) => (
  <div className={styles.toolbar} role="toolbar" aria-label="Document controls">
    {search && (
      <>
        <button
          type="button"
          className={`${styles.toolButton} ${search.open ? styles.toolButtonOn : ''}`}
          aria-label="Search document"
          aria-pressed={search.open}
          onClick={search.onToggle}
        >
          <Search aria-hidden="true" />
        </button>
        <span className={styles.divider} aria-hidden="true" />
      </>
    )}
    {page && (
      <>
        <PageInput current={page.current} total={page.total} onGo={page.onGo} />
        <span className={styles.divider} aria-hidden="true" />
      </>
    )}
    <button
      type="button"
      className={styles.toolButton}
      aria-label="Zoom out"
      disabled={!canZoomOut(zoom.value)}
      onClick={() => zoom.onChange(zoomOut(zoom.value))}
    >
      <Minus aria-hidden="true" />
    </button>
    <span className={styles.zoomLabel} aria-live="polite">
      {zoomLabel(zoom.value)}
    </span>
    <button
      type="button"
      className={styles.toolButton}
      aria-label="Zoom in"
      disabled={!canZoomIn(zoom.value)}
      onClick={() => zoom.onChange(zoomIn(zoom.value))}
    >
      <Plus aria-hidden="true" />
    </button>
  </div>
);

// Sits just above the toolbar while search is open. Enter goes to the next
// match, Shift+Enter to the previous, Esc closes search (not the viewer).
export const SearchBar = ({ inputRef, query, onQuery, count, active, preparing, onNext, onPrev, onClose }) => {
  let status = '';
  if (preparing) status = 'Searching…';
  else if (query.trim()) status = count ? `${active + 1} / ${count}` : 'No matches';

  return (
    <div className={styles.searchBar} data-viewer-search="">
      <Search className={styles.searchIcon} aria-hidden="true" />
      <input
        ref={inputRef}
        className={styles.searchInput}
        placeholder="Search document"
        aria-label="Search document"
        value={query}
        onChange={(e) => onQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (e.shiftKey) onPrev();
            else onNext();
          } else if (e.key === 'Escape') {
            onClose();
          }
        }}
      />
      <span className={styles.searchStatus} aria-live="polite">
        {status}
      </span>
      <button type="button" className={styles.toolButton} aria-label="Previous match" disabled={!count} onClick={onPrev}>
        <ChevronUp aria-hidden="true" />
      </button>
      <button type="button" className={styles.toolButton} aria-label="Next match" disabled={!count} onClick={onNext}>
        <ChevronDown aria-hidden="true" />
      </button>
      <button type="button" className={styles.toolButton} aria-label="Close search" onClick={onClose}>
        <X aria-hidden="true" />
      </button>
    </div>
  );
};
